import { useContext } from 'react';
import { UserContext } from '../contexts/UserContext';
import { DateFormats } from '../types/graphqlTypes';

const pad = (n: number) => String(n).padStart(2, '0');

// A moment as the API sends it (epoch milliseconds), or already a Date.
export type DateValue = Date | number | string;

const getDateParts = (value: DateValue) => {
  const d = new Date(value);
  return {
    dd: pad(d.getDate()),
    mm: pad(d.getMonth() + 1),
    yyyy: String(d.getFullYear()),
    hh: pad(d.getHours()),
    min: pad(d.getMinutes()),
  };
};

// Calendar days are stored in UTC (a subscription's last day ends at 23:59:59.999 UTC,
// renewals start at 00:00 UTC), so they are read in UTC wherever the viewer is.
const getUtcDayParts = (value: DateValue) => {
  const d = new Date(value);
  return { dd: pad(d.getUTCDate()), mm: pad(d.getUTCMonth() + 1), yyyy: String(d.getUTCFullYear()) };
};

export const useFormatDate = () => {
  const { dateFormat } = useContext(UserContext);

  const formatParts = ({ dd, mm, yyyy }: { dd: string; mm: string; yyyy: string }) => {
    switch (dateFormat) {
      case DateFormats.Ddmmyyyy:
        return `${dd}/${mm}/${yyyy}`;
      case DateFormats.Mmddyyyy:
        return `${mm}/${dd}/${yyyy}`;
      case DateFormats.Yyyymmdd:
        return `${yyyy}/${mm}/${dd}`;
      default:
        return `${dd}/${mm}/${yyyy}`;
    }
  };

  // A moment, shown as the viewer's local date.
  const formatDate = (value: DateValue) => formatParts(getDateParts(value));

  // A calendar day (end dates, renewals, expiries), the same day for every viewer.
  const formatDay = (value: DateValue) => formatParts(getUtcDayParts(value));

  const formatDateAndTime = (value: DateValue) => {
    const { hh, min } = getDateParts(value);
    return `${formatDate(value)} - ${hh}:${min}`;
  };

  return { formatDate, formatDay, formatDateAndTime };
};
